import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Calculator } from "lucide-react";
import { FLAT_RATE, calculateBill } from "../utils/billing";
import { formatKES } from "../utils/format";
import SeoHead from "../components/SeoHead";

/**
 * Pure-CSS "numbers falling in" animation for the headline price.
 * Each digit drops from above with a stagger, then the whole price
 * pulses once. Replays every ~7 s so the low price keeps catching
 * the eye. No motion library needed — all `@keyframes` live inline
 * so this component is self-contained and doesn't pollute
 * index.css.
 */
function AnimatedPrice() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 7000);
    return () => clearInterval(id);
  }, []);

  const digits = String(FLAT_RATE).split(""); // ["1", "0", "0"]

  return (
    <div
      key={tick} /* remount to retrigger CSS animations */
      className="relative mt-3 flex items-end justify-center gap-1 overflow-hidden"
      style={{ height: "5.5rem" }}
    >
      <style>{`
        @keyframes paysafi-kes-in {
          0%   { opacity: 0; transform: translateY(-16px) scale(0.9); }
          60%  { opacity: 1; transform: translateY(2px)  scale(1.02); }
          100% { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes paysafi-digit-drop {
          0%   { opacity: 0; transform: translateY(-80px) rotate(-10deg); }
          60%  { opacity: 1; transform: translateY(8px)   rotate(2deg); }
          80%  {              transform: translateY(-3px) rotate(-1deg); }
          100% { opacity: 1; transform: translateY(0)     rotate(0); }
        }
        @keyframes paysafi-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.04); }
        }
      `}</style>
      <span
        className="text-2xl font-semibold text-indigo-500 dark:text-indigo-400"
        style={{
          alignSelf: "flex-end",
          marginBottom: "0.55rem",
          marginRight: "0.25rem",
          opacity: 0,
          animation: "paysafi-kes-in 0.45s ease-out 0.05s forwards",
        }}
      >
        KES
      </span>
      <div
        className="flex items-end"
        style={{
          animation:
            "paysafi-pulse 0.6s ease-in-out " +
            `${0.4 + digits.length * 0.12}s 1`,
        }}
      >
        {digits.map((d, i) => (
          <span
            key={i}
            className="inline-block text-6xl font-bold leading-none text-indigo-700 dark:text-indigo-300 sm:text-7xl"
            style={{
              opacity: 0,
              animation: `paysafi-digit-drop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) ${
                0.25 + i * 0.12
              }s forwards`,
            }}
          >
            {d}
          </span>
        ))}
      </div>
    </div>
  );
}

const pricingJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Paysafi",
  description:
    "Rent tracking and tenant management platform for Kenyan landlords, with SMS reminders and AI voice call automation.",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "PropertyManagement",
  operatingSystem: "Web",
  url: "https://paysafi.com",
  image: "https://paysafi.com/og-image.png",
  publisher: { "@type": "Organization", name: "Paysafi" },
  offers: [
    {
      "@type": "Offer",
      name: "Per active tenant",
      price: FLAT_RATE,
      priceCurrency: "KES",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: FLAT_RATE,
        priceCurrency: "KES",
        unitText: "tenant/month",
      },
      availability: "https://schema.org/InStock",
      url: "https://paysafi.com/pricing",
    },
  ],
};

export default function PricingPage() {
  const [tenantCount, setTenantCount] = useState(50);
  const bill = useMemo(() => calculateBill(tenantCount), [tenantCount]);

  return (
    <article>
      <SeoHead
        title="Pricing"
        description={`Simple per-tenant pricing in Kenyan Shillings. ${formatKES(FLAT_RATE)} per active tenant per month, no matter how many tenants you have. No hidden fees.`}
        path="/pricing"
        jsonLd={pricingJsonLd}
      />
      <section className="border-b border-gray-100 bg-gray-50 py-16 dark:border-gray-800 dark:bg-gray-900 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
            Simple per-tenant pricing
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            One flat rate. {formatKES(FLAT_RATE)} per active tenant per month,
            whether you manage one tenant or a thousand. No tiers, no hidden
            fees.
          </p>
        </div>
      </section>

      {/* Headline price card */}
      <section className="border-b border-gray-100 py-12 dark:border-gray-800 sm:py-16">
        <div className="mx-auto max-w-lg px-4 sm:px-6">
          <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-8 text-center dark:border-indigo-800 dark:bg-indigo-950">
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
              Paysafi Pro
            </div>
            <AnimatedPrice />
            <div className="mt-1 text-sm text-indigo-600 dark:text-indigo-400">
              / active tenant / month
            </div>
            <p className="mt-4 text-sm text-indigo-700 dark:text-indigo-300">
              Billing is based on the highest number of active tenants during
              each monthly cycle.
            </p>
            <p className="mt-3 inline-block rounded-full bg-indigo-600 px-4 py-1 text-sm font-semibold text-white dark:bg-indigo-500">
              1 month free trial
            </p>
          </div>
        </div>
      </section>

      {/* Pricing calculator */}
      <section className="border-b border-gray-100 py-12 dark:border-gray-800 sm:py-16">
        <div className="mx-auto max-w-lg px-4 sm:px-6">
          <div className="flex items-center justify-center gap-2">
            <Calculator className="h-5 w-5 text-indigo-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Estimate your cost
            </h2>
          </div>
          <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <label className="label-base">Number of active tenants</label>
            <input
              type="range"
              min="1"
              max="1000"
              value={tenantCount}
              onChange={(e) => setTenantCount(Number(e.target.value))}
              className="mt-2 w-full accent-indigo-600"
            />
            <div className="mt-2 flex items-center justify-between">
              <input
                type="number"
                min="1"
                max="10000"
                value={tenantCount}
                onChange={(e) =>
                  setTenantCount(Math.max(1, Number(e.target.value) || 1))
                }
                className="input-base w-24 text-center"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                tenants
              </span>
            </div>

            <div className="mt-6 space-y-3 border-t border-gray-100 pt-5 dark:border-gray-700">
              <Row label="Rate" value={`${formatKES(FLAT_RATE)} / tenant`} />
              <Row label="Peak tenants" value={tenantCount} />
              <Row label="Subtotal" value={formatKES(bill.subtotal)} />
              <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
                <span className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                  Estimated monthly cost
                </span>
                <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400 sm:text-2xl">
                  {formatKES(bill.total)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="text-center text-2xl font-bold text-gray-900 dark:text-white">
            Everything included
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Every plan includes the full Paysafi feature set. No feature gating.
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {[
              "Unlimited properties",
              "Rent tracking & dashboard",
              "Partial and multi month payments",
              "Automatic overdue detection",
              "SMS reminders",
              "AI voice call reminders",
              "Tenant notes & history",
              "Payment audit trail",
              "Bulk actions",
              "Property level views",
              "Priority support",
            ].map((f) => (
              <li
                key={f}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
              >
                <Check className="h-4 w-4 flex-none text-indigo-600" />
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-10 text-center">
            <Link to="/signup" className="btn-primary text-base px-6 py-3">
              Get started
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="font-medium text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}
